import { useState, type MutableRefObject } from "react";
import { toast } from "sonner";
import { flipCoin, higherOrLower, oddOrEven, ordinal, pickNumber, raffleDraw, redOrBlack, rollDice, thisOrThat, validateRange, type ToolOutcome } from "../../engine/tools";
import { randomInt } from "../../engine/random";
import type { ToolId } from "../../data/tools";
import { PopButton } from "../PopButton";

export interface ToolCtx {
  title: string;
  emit: (o: ToolOutcome, title?: string) => void;
  againRef: MutableRefObject<() => void>;
  close: () => void;
}

function guard(fn: () => void) {
  try {
    fn();
  } catch (e: any) {
    toast.error(e.message);
  }
}

function SimpleTool({ ctx, label, run, testId }: { ctx: ToolCtx; label: string; run: () => ToolOutcome; testId: string }) {
  const go = () => ctx.emit(run());
  ctx.againRef.current = go;
  return <PopButton tone="orange" size="xl" className="w-full" onClick={go} data-testid={testId}>{label}</PopButton>;
}

function DiceTool({ ctx }: { ctx: ToolCtx }) {
  const [count, setCount] = useState(1);
  const go = () => ctx.emit(rollDice(count));
  ctx.againRef.current = go;
  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-sm font-extrabold uppercase tracking-wide">How many dice?</p>
        <div className="flex gap-2">
          {[1, 2, 3].map((n) => (
            <button key={n} className="chip px-5 text-lg" data-active={count === n} onClick={() => setCount(n)} data-testid={`dice-count-${n}`}>{n}</button>
          ))}
        </div>
      </div>
      <PopButton tone="pink" size="xl" className="w-full" onClick={go} data-testid="tool-roll-button">Roll it</PopButton>
    </div>
  );
}

function ThisThatTool({ ctx }: { ctx: ToolCtx }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const go = () => guard(() => ctx.emit(thisOrThat(a, b), `${a.trim()} or ${b.trim()}?`));
  ctx.againRef.current = go;
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <input className="field" maxLength={80} placeholder="This… (e.g. Stay in)" value={a} onChange={(e) => setA(e.target.value)} data-testid="this-or-that-input-a" />
      <p className="text-center font-display text-xl font-bold uppercase">or</p>
      <input className="field" maxLength={80} placeholder="That… (e.g. Go out)" value={b} onChange={(e) => setB(e.target.value)} data-testid="this-or-that-input-b" />
      <PopButton type="submit" tone="purple" size="xl" className="mt-2 w-full" data-testid="tool-decide-button">Decide</PopButton>
    </form>
  );
}

export function PickNumberTool({ ctx }: { ctx: ToolCtx }) {
  const [min, setMin] = useState("1");
  const [max, setMax] = useState("10");
  const go = () => guard(() => { const r = validateRange(min, max); ctx.emit(pickNumber(r.min, r.max)); });
  ctx.againRef.current = go;
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm font-extrabold uppercase">From<input className="field mt-1" inputMode="numeric" value={min} onChange={(e) => setMin(e.target.value)} data-testid="pick-number-min-input" /></label>
        <label className="text-sm font-extrabold uppercase">To<input className="field mt-1" inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value)} data-testid="pick-number-max-input" /></label>
      </div>
      <PopButton type="submit" tone="green" size="xl" className="w-full" data-testid="tool-pick-button">Pick</PopButton>
    </form>
  );
}

export const DEFAULT_PRIZES = ["Golden Hamper", "Silver Gift Box", "Bronze Treat Bag", "Sweet Treat", "Lucky Dip"];

function RaffleTool({ ctx }: { ctx: ToolCtx }) {
  const [tickets, setTickets] = useState("100");
  const [count, setCount] = useState(3);
  const [prizes, setPrizes] = useState(DEFAULT_PRIZES);
  const go = () => guard(() => {
    const n = Number(tickets);
    if (!Number.isInteger(n) || n < 1 || n > 100000) throw new Error("Enter a whole number of tickets from 1 to 100,000.");
    if (n < count) throw new Error(`You need at least ${count} tickets for ${count} prizes.`);
    ctx.emit(raffleDraw(n, prizes.slice(0, count).map((p, i) => p.trim() || DEFAULT_PRIZES[i])));
  });
  ctx.againRef.current = go;
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); go(); }}>
      <label className="block text-sm font-extrabold uppercase">Number of tickets
        <input className="field mt-1" inputMode="numeric" value={tickets} onChange={(e) => setTickets(e.target.value)} data-testid="raffle-tickets-input" />
      </label>
      <div>
        <p className="mb-2 text-sm font-extrabold uppercase tracking-wide">How many prizes?</p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} className="chip px-4 text-lg" data-active={count === n} onClick={() => setCount(n)} data-testid={`raffle-prize-count-${n}`}>{n}</button>)}
        </div>
      </div>
      <div className="space-y-2">
        {prizes.slice(0, count).map((p, i) => (
          <label key={i} className="flex items-center gap-2 text-sm font-extrabold uppercase">
            <span className="w-10 shrink-0">{ordinal(i + 1)}</span>
            <input className="field" maxLength={40} value={p} placeholder={DEFAULT_PRIZES[i]} onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`raffle-prize-input-${i + 1}`} />
          </label>
        ))}
      </div>
      <PopButton type="submit" tone="green" size="xl" className="w-full" data-testid="raffle-draw-button">Draw winners</PopButton>
    </form>
  );
}

function HigherLowerTool({ ctx }: { ctx: ToolCtx }) {
  const [current, setCurrent] = useState(() => randomInt(1, 100));
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(0);
  const [next, setNext] = useState<number | null>(null);
  ctx.againRef.current = () => {
    if (next !== null) setCurrent(next);
    setNext(null);
    ctx.close();
  };
  const guess = (g: "higher" | "lower") => {
    const o = higherOrLower(current, g);
    const s = o.correct ? streak + 1 : 0;
    setStreak(s);
    setBest((b) => Math.max(b, s));
    setNext(o.next);
    ctx.emit({ ...o, detail: `${o.detail} ${o.correct ? `Streak: ${s}` : ""}`.trim() }, `From ${current}… ${g}?`);
  };
  return (
    <div className="space-y-5 text-center">
      <p className="text-sm font-extrabold uppercase tracking-widest text-zinc-500">Current number</p>
      <p className="comic-title text-8xl text-pop-yellow" data-testid="higher-lower-current">{current}</p>
      <div className="flex justify-center gap-3 text-sm font-extrabold uppercase">
        <span className="chip" data-testid="higher-lower-streak">Streak {streak}</span>
        <span className="chip" data-testid="higher-lower-best">Best {best}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <PopButton tone="green" size="lg" onClick={() => guess("higher")} data-testid="higher-button">Higher</PopButton>
        <PopButton tone="pink" size="lg" onClick={() => guess("lower")} data-testid="lower-button">Lower</PopButton>
      </div>
    </div>
  );
}

export function ToolControls({ id, ctx }: { id: ToolId; ctx: ToolCtx }) {
  switch (id) {
    case "heads-or-tails": return <SimpleTool ctx={ctx} label="Flip it" run={flipCoin} testId="tool-flip-button" />;
    case "roll-the-dice": return <DiceTool ctx={ctx} />;
    case "red-or-black": return <SimpleTool ctx={ctx} label="Draw a card" run={redOrBlack} testId="tool-draw-button" />;
    case "odd-or-even": return <SimpleTool ctx={ctx} label="Odd or even?" run={oddOrEven} testId="tool-odd-even-button" />;
    case "this-or-that": return <ThisThatTool ctx={ctx} />;
    case "raffle-draw": return <RaffleTool ctx={ctx} />;
    case "higher-or-lower": return <HigherLowerTool ctx={ctx} />;
    default: return null;
  }
}
